import * as React from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { StatusPill } from '@/components/ui/status-pill'

/*
 * Section: a collapsible grouping. A collapsed header states its current
 * summary, and dirty or invalid state is exposed in text as well as styling.
 */
function Section({
  title,
  summary,
  open,
  onOpenChange,
  state,
  headingLevel = 3,
  children,
}: {
  title: string
  summary?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  state?: { tone: 'success' | 'warning' | 'danger' | 'info' | 'neutral'; label: string }
  /** The header's heading level: one below the heading the section sits under. */
  headingLevel?: 2 | 3
  children: React.ReactNode
}) {
  const contentId = React.useId()
  const Heading = headingLevel === 2 ? 'h2' : 'h3'
  return (
    <div
      className={cn(
        'overflow-hidden rounded-md border bg-surface transition-colors',
        state?.tone === 'danger' ? 'border-danger/60' : 'border-line',
      )}
    >
      <Heading>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={contentId}
          onClick={() => onOpenChange(!open)}
          className="flex w-full items-center gap-3 px-5 py-3.5 text-left hover:bg-surface-hover focus-visible:-outline-offset-2"
        >
          <ChevronDown
            aria-hidden="true"
            className={cn(
              'size-5 shrink-0 text-fg-muted transition-transform',
              open ? '' : '-rotate-90',
            )}
          />
          <span className="flex-1">
            <span className="block text-sm text-fg-strong" style={{ fontWeight: 650 }}>
              {title}
            </span>
            {!open && summary ? (
              <span className="mt-0.5 block text-xs text-fg-muted">{summary}</span>
            ) : null}
          </span>
          {state ? <StatusPill tone={state.tone}>{state.label}</StatusPill> : null}
        </button>
      </Heading>
      {open ? (
        <div id={contentId} className="border-t border-line px-5 py-4">
          {children}
        </div>
      ) : null}
    </div>
  )
}

export { Section }
